import { Card } from "@/components/ui/card";
import { TrendingUp, Eye, CheckCircle2, Percent, FileText, UserCheck, MousePointerClick } from "lucide-react";

interface HeadlineStatsCardProps {
  totalImpressions: number;
  totalConversions: number;
  totalQuizOnly: number;
  totalLeads: number;
  totalB2CExits: number;
  totalCtaClicks: number;
  averageRate: number;
  averageQuizRate: number;
  averageLeadRate: number;
  averageB2CExitRate: number;
  averageCtaClickRate: number;
  adminImpressions: number;
  adminConversions: number;
  adminQuizOnly: number;
  adminLeads: number;
  adminB2CExits: number;
  adminCtaClicks: number;
  topVariant?: {
    id: string;
    headline: string;
    rate: number;
  };
}

export const HeadlineStatsCard = ({
  totalImpressions,
  totalConversions,
  totalQuizOnly,
  totalLeads,
  totalB2CExits,
  totalCtaClicks,
  averageRate,
  averageQuizRate,
  averageLeadRate,
  averageB2CExitRate,
  averageCtaClickRate,
  adminImpressions,
  adminConversions,
  adminQuizOnly,
  adminLeads,
  adminB2CExits,
  adminCtaClicks,
  topVariant
}: HeadlineStatsCardProps) => {
  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-8 mb-6">
      <Card className="p-6">
        <div className="flex items-center gap-2 text-muted-foreground mb-2">
          <Eye className="h-4 w-4" />
          <span className="text-sm font-medium">Łączne wyświetlenia</span>
        </div>
        <div className="text-2xl font-bold">{totalImpressions.toLocaleString()}</div>
        {adminImpressions > 0 && (
          <div className="text-[10px] text-muted-foreground/60 mt-1.5 flex items-center gap-1">
            <span className="px-1.5 py-0.5 bg-muted/50 rounded text-[9px] font-medium">admin</span>
            <span>{adminImpressions.toLocaleString()}</span>
          </div>
        )}
      </Card>

      <Card className="p-6">
        <div className="flex items-center gap-2 text-muted-foreground mb-2">
          <CheckCircle2 className="h-4 w-4" />
          <span className="text-sm font-medium">Łączne konwersje</span>
        </div>
        <div className="text-2xl font-bold">{totalConversions.toLocaleString()}</div>
        <div className="text-xs text-muted-foreground mt-1">{averageRate.toFixed(2)}% CR</div>
        {adminConversions > 0 && (
          <div className="text-[10px] text-muted-foreground/60 mt-1.5 flex items-center gap-1">
            <span className="px-1.5 py-0.5 bg-muted/50 rounded text-[9px] font-medium">admin</span>
            <span>{adminConversions.toLocaleString()}</span>
          </div>
        )}
      </Card>

      <Card className="p-6">
        <div className="flex items-center gap-2 text-muted-foreground mb-2">
          <FileText className="h-4 w-4" />
          <span className="text-sm font-medium">Konwersje Quiz</span>
        </div>
        <div className="text-2xl font-bold text-blue-600">{totalQuizOnly.toLocaleString()}</div>
        <div className="text-xs text-muted-foreground mt-1">{averageQuizRate.toFixed(2)}% CR</div>
        {adminQuizOnly > 0 && (
          <div className="text-[10px] text-muted-foreground/60 mt-1.5 flex items-center gap-1">
            <span className="px-1.5 py-0.5 bg-muted/50 rounded text-[9px] font-medium">admin</span>
            <span>{adminQuizOnly.toLocaleString()}</span>
          </div>
        )}
      </Card>

      <Card className="p-6">
        <div className="flex items-center gap-2 text-muted-foreground mb-2">
          <UserCheck className="h-4 w-4" />
          <span className="text-sm font-medium">Konwersje Lead</span>
        </div>
        <div className="text-2xl font-bold text-green-600">{totalLeads.toLocaleString()}</div>
        <div className="text-xs text-muted-foreground mt-1">{averageLeadRate.toFixed(2)}% CR</div>
        {adminLeads > 0 && (
          <div className="text-[10px] text-muted-foreground/60 mt-1.5 flex items-center gap-1">
            <span className="px-1.5 py-0.5 bg-muted/50 rounded text-[9px] font-medium">admin</span>
            <span>{adminLeads.toLocaleString()}</span>
          </div>
        )}
      </Card>

      <Card className="p-6 bg-orange-50/50 dark:bg-orange-950/20 border-orange-200 dark:border-orange-900">
        <div className="flex items-center gap-2 text-orange-700 dark:text-orange-400 mb-2">
          <span className="text-xl">🚪</span>
          <span className="text-sm font-medium">Wyjścia B2C (Q2)</span>
        </div>
        <div className="text-2xl font-bold text-orange-600 dark:text-orange-500">{totalB2CExits.toLocaleString()}</div>
        <div className="text-xs text-orange-600/70 dark:text-orange-400/70 mt-1">{averageB2CExitRate.toFixed(2)}%</div>
        {adminB2CExits > 0 && (
          <div className="text-[10px] text-muted-foreground/60 mt-1.5 flex items-center gap-1">
            <span className="px-1.5 py-0.5 bg-muted/50 rounded text-[9px] font-medium">admin</span>
            <span>{adminB2CExits.toLocaleString()}</span>
          </div>
        )}
      </Card>

      <Card className="p-6">
        <div className="flex items-center gap-2 text-muted-foreground mb-2">
          <MousePointerClick className="h-4 w-4" />
          <span className="text-sm font-medium">Kliknięcia CTA</span>
        </div>
        <div className="text-2xl font-bold text-purple-600">{totalCtaClicks.toLocaleString()}</div>
        <div className="text-xs text-muted-foreground mt-1">{averageCtaClickRate.toFixed(2)}% CTR</div>
        {adminCtaClicks > 0 && (
          <div className="text-[10px] text-muted-foreground/60 mt-1.5 flex items-center gap-1">
            <span className="px-1.5 py-0.5 bg-muted/50 rounded text-[9px] font-medium">admin</span>
            <span>{adminCtaClicks.toLocaleString()}</span>
          </div>
        )}
      </Card>

      <Card className="p-6 col-span-2">
        <div className="flex items-center gap-2 text-muted-foreground mb-2">
          <TrendingUp className="h-4 w-4" />
          <span className="text-sm font-medium">Najlepszy wariant</span>
        </div>
        {topVariant ? (
          <>
            <div className="text-lg font-bold text-primary">{topVariant.rate.toFixed(2)}%</div>
            <div className="text-xs text-muted-foreground mt-1">
              <div className="font-mono text-[10px] text-muted-foreground/70 mb-0.5">
                ID: {topVariant.id.slice(0, 8)}
              </div>
              <div className="truncate">
                {topVariant.headline}
              </div>
            </div>
          </>
        ) : (
          <div className="text-sm text-muted-foreground">Brak danych</div>
        )}
      </Card>
    </div>
  );
};
